import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import { BrandMark } from "@/components/BrandMark";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";

const perks = ["One tap sends a professional PDF invoice", "Auto invoice numbers, dates & tax math", "Every client saved with their standard job & price"];

export default function AuthPage() {
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [f, setF] = useState({ email: "", password: "", business_name: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/clients" replace />;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      if (mode === "login") await login(f.email, f.password);
      else await register(f.email, f.password, f.business_name);
      navigate("/clients");
    } catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-[#0B0F17] text-white p-12">
        <div className="flex items-center gap-3">
          <BrandMark size={40} />
          <span className="font-heading font-bold text-xl">Basta10</span>
        </div>
        <div>
          <h1 className="font-heading text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.05]">Finish the job.<br />Send the invoice.<br /><span className="text-amber-400">Before you leave the driveway.</span></h1>
          <ul className="mt-10 space-y-3">
            {perks.map((p) => <li key={p} className="flex items-center gap-3 text-slate-300"><Check className="h-4 w-4 text-emerald-400" />{p}</li>)}
          </ul>
        </div>
        <p className="text-xs text-slate-500">Built for lawn care, cleaning, handyman and every field pro.</p>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm fade-up">
          <div className="lg:hidden mb-8">
            <img src="/logo-wordmark.png" alt="Basta10 — Invoice in Seconds" className="h-28 w-auto -ml-1" data-testid="auth-wordmark" />
          </div>
          <div className="grid grid-cols-2 p-1 rounded-lg bg-secondary mb-6">
            {["login", "register"].map((m) => (
              <button key={m} type="button" data-testid={`auth-${m}-tab`} onClick={() => { setMode(m); setErr(""); }}
                className={`h-9 rounded-md text-sm font-semibold transition-colors ${mode === m ? "bg-card shadow-sm" : "text-muted-foreground"}`}>
                {m === "login" ? "Sign in" : "Create account"}
              </button>
            ))}
          </div>
          <h2 className="font-heading text-2xl font-bold tracking-tight">{mode === "login" ? "Welcome back" : "Set up your business"}</h2>
          <p className="text-sm text-muted-foreground mt-1 mb-6">{mode === "login" ? "Sign in to send invoices." : "Free to start. Your clients and invoices stay private to you."}</p>
          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div className="space-y-1.5">
                <Label>Business name</Label>
                <Input data-testid="auth-register-business-input" required value={f.business_name} onChange={set("business_name")} placeholder="Green Edge Lawn Care" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input data-testid="auth-login-email-input" type="email" required value={f.email} onChange={set("email")} placeholder="you@business.com" />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label>Password</Label>
                {mode === "login" && <Link to="/forgot-password" data-testid="auth-forgot-password-link" className="text-xs font-semibold text-emerald-600 hover:text-emerald-700">Forgot password?</Link>}
              </div>
              <Input data-testid="auth-login-password-input" type="password" required minLength={6} value={f.password} onChange={set("password")} placeholder="••••••••" />
            </div>
            {err && <p data-testid="auth-error-message" className="text-sm text-red-600 font-medium">{err}</p>}
            <Button data-testid="auth-login-submit-button" type="submit" disabled={busy} className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-bold">
              {busy ? "Please wait..." : mode === "login" ? "Sign in" : "Create account"}
            </Button>
          </form>
          <p className="mt-8 text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-1" data-testid="auth-legal-links">
            <Link to="/privacy" className="hover:text-foreground">Privacy</Link>
            <Link to="/terms" className="hover:text-foreground">Terms</Link>
            <Link to="/support" className="hover:text-foreground">Support</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
